import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { filter, take, map, switchMap } from 'rxjs/operators';
import Cookies from 'js-cookie';

import { User } from '@app/models/users/users.model';
import { JwtService } from '@app/utils/jwt.service';
import { env } from '@envs/env';

const category = 'OAuth';

// eslint-disable-next-line
export enum AuthStatus {
    AUTH_INIT = 'INIT',
    AUTH_COMPLETE = 'COMPLETE',
    AUTH_VALID = 'VALID',
    AUTH_INVALID = 'INVALID',
    AUTH_NO_ACCESS = 'NO_ACCESS',
    AUTH_NO_REFRESH = 'NO_REFRESH',
    AUTH_REFRESHING = 'REFRESHING',
    AUTH_IN_PROGRESS = 'IN_PROGRESS',
}

export class ChangeStatus {
    static readonly type = `[${category}] ChangeStatus`;
    constructor(public status: AuthStatus) {}
}

export class SetAuthData {
    static readonly type = `[${category}] SetAuthData`;
    constructor(public url: string, public scopes: string[], public user: Partial<User>) {}
}

export class UpdateUser {
    static readonly type = `[${category}] UpdateUser`;
    constructor(public user: Partial<User>) {}
}

interface IAuthState {
    status: AuthStatus;
    baseURL: string;
    scopes: string[];
    user: Partial<User>;
}

@State<IAuthState>({
    name: 'auth',
    defaults: {
        status: AuthStatus.AUTH_INIT,
        baseURL: '',
        scopes: [],
        user: null,
    },
})
export class AuthState {
    constructor(private store: Store, private jwt: JwtService) {}

    @Selector([AuthState])
    static status(state: IAuthState) {
        return state.status;
    }

    @Selector([AuthState])
    static baseURL(state: IAuthState) {
        return state.baseURL;
    }

    @Selector([AuthState])
    static hasScope(state: IAuthState) {
        return (scope: string) => state.scopes.indexOf(scope.split(':')[0]) >= 0 || state.scopes.indexOf(scope) >= 0;
    }

    @Selector([AuthState])
    static user(state: IAuthState) {
        return state.user;
    }

    @Action(ChangeStatus)
    changeStatus(ctx: StateContext<IAuthState>, action: ChangeStatus) {
        ctx.patchState({
            status: action.status,
        });
    }

    @Action(SetAuthData)
    setAuthData(ctx: StateContext<IAuthState>, action: SetAuthData) {
        ctx.patchState({
            baseURL: action.url,
            scopes: action.scopes,
            user: action.user,
        });
    }

    @Action(UpdateUser)
    updateUser(ctx: StateContext<IAuthState>, action: SetAuthData) {
        ctx.patchState({
            user: action.user,
        });
    }

    emitAuthComplete() {
        return this.store.select(AuthState.status).pipe(filter((status) => status === AuthStatus.AUTH_COMPLETE));
    }

    emitAuthInvalid() {
        return this.store.select(AuthState.status).pipe(filter((status) => status === AuthStatus.AUTH_INVALID));
    }

    baseURL() {
        return this.store.selectSnapshot(AuthState.baseURL);
    }

    userID() {
        return this.store.selectSnapshot(AuthState.user).id;
    }

    getUser() {
        return this.store.select(AuthState.user).pipe(
            filter((user) => !!user && user.id !== 0),
            take(1)
        );
    }
}
